import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { keychainService } from '../services/keychainService';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerDeviceToken,
  setupIOSPushListeners
} from '../services/notificationService';
import { requestOpenMoneyClub } from '../components/community/MoneyClub';

const NotificationContext = createContext();

// ── In-app toast banner for foreground notifications ──
const TOAST_DURATION = 6000;

const toastStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 99999,
    padding: '0 12px',
    paddingTop: 'env(safe-area-inset-top, 12px)',
  },
  container: (visible) => ({
    pointerEvents: visible ? 'auto' : 'none',
    transform: visible ? 'translateY(8px)' : 'translateY(-100%)',
    opacity: visible ? 1 : 0,
    transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease',
    background: 'rgba(255, 255, 255, 0.72)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    backdropFilter: 'saturate(180%) blur(20px)',
    borderRadius: '16px',
    padding: '14px 16px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    touchAction: 'pan-y',
  }),
  icon: {
    fontSize: '24px',
    flexShrink: 0,
    lineHeight: 1,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2px',
  },
  title: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#000000',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  timestamp: {
    fontSize: '13px',
    color: 'rgba(0,0,0,0.4)',
    flexShrink: 0,
    marginLeft: '8px',
  },
  body: {
    fontSize: '14px',
    color: 'rgba(0,0,0,0.6)',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
};

const ToastNotification = ({ toast, onDismiss }) => {
  const touchStartY = useRef(null);

  if (!toast) return null;

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    // Swiped up by 30px or more — dismiss
    if (delta < -30) {
      touchStartY.current = null;
      onDismiss();
    }
  };

  const handleTouchEnd = () => {
    touchStartY.current = null;
  };

  return (
    <div style={toastStyles.overlay} onClick={onDismiss}>
      <div
        style={toastStyles.container(!!toast)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div style={toastStyles.icon}>🔔</div>
        <div style={toastStyles.content}>
          <div style={toastStyles.header}>
            <div style={toastStyles.title}>{toast.title || 'Worthly'}</div>
            <div style={toastStyles.timestamp}>now</div>
          </div>
          <div style={toastStyles.body}>{toast.body}</div>
        </div>
      </div>
    </div>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children, userId, userData, saveUserData }) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [deviceToken, setDeviceToken] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // Check support and current permission on mount
  useEffect(() => {
    const supported = isNotificationSupported();
    setIsSupported(supported);

    if (!Capacitor.isNativePlatform() && supported) {
      setPermission(getNotificationPermission());
    }
  }, []);

  // Set up iOS push listeners and auto-register if already enabled
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !userId) return;

    const { PushNotifications } = require('@capacitor/push-notifications');
    const listeners = [];

    // Set up listeners FIRST, then register
    const setupListeners = async () => {
      const regListener = await PushNotifications.addListener('registration', async (token) => {
        console.log('APNs token received:', token.value);

        // Guard: make sure we have a session token before calling backend
        const sessionToken = keychainService.getCached();
        if (!sessionToken) {
          console.warn('No session_token in Keychain cache — waiting 2s and retrying...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retryToken = keychainService.getCached();
          if (!retryToken) {
            console.error('Still no session_token after retry. Cannot register device token.');
            setDeviceToken(token.value); // Save locally so we can retry later
            setPermission('granted');
            return;
          }
        }

        const { registerDeviceToken } = require('../services/notificationService');
        const result = await registerDeviceToken(userId, token.value, 'ios');
        console.log('registerDeviceToken result:', JSON.stringify(result));

        if (result && result.success !== false) {
          console.log('Device token registered successfully with backend');
        } else {
          console.error('Device token registration FAILED:', JSON.stringify(result));
        }

        setDeviceToken(token.value);
        setPermission('granted');
      });
      listeners.push(regListener);

      const errListener = await PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration FAILED:', JSON.stringify(error));
      });
      listeners.push(errListener);

      const fgListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push received in foreground:', JSON.stringify(notification));
        // Foreground presentation is handled natively in AppDelegate.swift, which
        // shows the real iOS banner. Raising the in-app toast here as well would
        // mean two alerts for one notification, so this listener only logs.
        //
        // The toast component and its state are still here and still work - if
        // you want it back, call setToast({ title, body }) again.
      });
      listeners.push(fgListener);

      const tapListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification tapped:', JSON.stringify(notification));
        const data = (notification && notification.notification && notification.notification.data) || {};
        const category = data.category || (data.aps && data.aps.category);
        if (category === 'MONEY_CLUB') requestOpenMoneyClub();
      });
      listeners.push(tapListener);
    };

    // Auto-register after listeners are ready
    const autoRegister = async () => {
      await setupListeners();

      if (userData?.notification_preferences?.push_enabled) {
        try {
          const permResult = await PushNotifications.checkPermissions();
          console.log('iOS push permission status:', permResult.receive);
          if (permResult.receive === 'granted') {
            setTimeout(async () => {
              await PushNotifications.register();
              console.log('Called PushNotifications.register()');
            }, 500);
          } else if (permResult.receive === 'prompt') {
            const reqResult = await PushNotifications.requestPermissions();
            if (reqResult.receive === 'granted') {
              setTimeout(async () => {
                await PushNotifications.register();
                console.log('Push permission granted, called register()');
              }, 500);
            }
          }
        } catch (e) {
          console.error('Auto-register push error:', e);
        }
      }
    };

    autoRegister();

    // Cleanup: remove all listeners when userId changes or component unmounts
    return () => {
      listeners.forEach(l => l.remove());
    };
  }, [userId, userData?.notification_preferences?.push_enabled]);

  // Sync hasRequested from user's DB data
  useEffect(() => {
    if (userData?.notification_prompt_shown) {
      setHasRequested(true);
    }
  }, [userData?.notification_prompt_shown]);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (hasRequested) {
      return { success: false, reason: 'already_requested' };
    }

    const result = await requestNotificationPermission();
    setPermission(result.permission);
    setHasRequested(true);

    // Save preferences to account
    if (saveUserData) {
      saveUserData({
        notification_prompt_shown: true,
        notification_preferences: {
          push_enabled: true,
          spending: true,
          reminders: true,
          achievements: true,
          general: true,
        }
      });
    }

    // On web, register a web token
    if (!Capacitor.isNativePlatform() && result.success && userId) {
      const webToken = `web_${userId}_${Date.now()}`;
      setDeviceToken(webToken);
      await registerDeviceToken(userId, webToken, 'web');
    }
    // On iOS, the registration listener in setupIOSPushListeners handles the token automatically

    return result;
  }, [hasRequested, userId, saveUserData]);

  // Mark as shown when user skips
  const dismissPrompt = useCallback(() => {
    setHasRequested(true);
    if (saveUserData) {
      saveUserData({ notification_prompt_shown: true });
    }
  }, [saveUserData]);

  // Check if we should show the permission prompt
  const shouldShowPermissionPrompt = useCallback(() => {
    return false; // Handled in onboarding flow
  }, [hasRequested]);

  // Dismiss the in-app toast
  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  const value = {
    permission,
    isSupported,
    hasRequested,
    deviceToken,
    requestPermission,
    dismissPrompt,
    shouldShowPermissionPrompt,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastNotification toast={toast} onDismiss={dismissToast} />
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
